/**
 * One delivery day as every page of its packet reads it: the stops numbered
 * in route order, the route's day, and the day's words. Pure.
 */

import type { DeliveryRouteDay, DeliveryRouteResponse } from '@/api/delivery-route'
import type { PrintDay } from '@/lib/chefs/print-plan'
import type { Campus } from '@/lib/chefs/types'

import { type PacketStop, packetStops } from './stops'
import type { DateBox } from './units'

const SHORT_WEEKDAY = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const
const SHORT_MONTH = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'] as const

/** "Thu Oct 15" for a local-midnight day. */
export function packetDateShort(date: Date): string {
  return `${SHORT_WEEKDAY[date.getDay()]} ${SHORT_MONTH[date.getMonth()]} ${date.getDate()}`
}

/** "Thursday, October 15". */
export function packetDateLong(date: Date): string {
  return date.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })
}

/** The title's date box for a local-midnight day: "THU" over "OCT 15". */
export function packetDateBox(date: Date): DateBox {
  return {
    weekday: SHORT_WEEKDAY[date.getDay()]!.toUpperCase(),
    date: `${SHORT_MONTH[date.getMonth()]!.toUpperCase()} ${date.getDate()}`,
  }
}

/**
 * The date stamp of a list that covers several days (a multi-day print's one shopping list): "TUE–FRI" over
 * "OCT 20–23", "FRI–TUE" over "OCT 30–NOV 3" across a month; one day's stamp when the range is a single day.
 */
export function packetRangeDateBox(first: Date, last: Date): DateBox {
  const from = packetDateBox(first)
  const to = packetDateBox(last)
  if (first.toDateString() === last.toDateString()) {
    return from
  }
  const sameMonth = first.getFullYear() === last.getFullYear() && first.getMonth() === last.getMonth()
  return {
    weekday: `${from.weekday}–${to.weekday}`,
    date: sameMonth ? `${from.date}–${last.getDate()}` : `${from.date}–${to.date}`,
  }
}

export interface PacketDay {
  iso: string
  date: Date
  campuses: Campus[]
  /** The day's stops, numbered 1, 2, 3… in route order. */
  stops: PacketStop[]
  /** The route's day; null when the route is unknown or drives nothing that day. */
  routeDay: DeliveryRouteDay | null
  kitchenName: string
  /** "Thu Oct 15". */
  dateShort: string
  /** "THU OCT 15", for the black bands. */
  dateCaps: string
  /** "Thursday, October 15". */
  dateLong: string
  /** "THU" over "OCT 15", on every daily page's title. */
  dateBox: DateBox
}

export function packetDay(day: PrintDay, route: DeliveryRouteResponse | null, kitchenName: string): PacketDay {
  const routeDay = route?.days.find((d) => d.date === day.iso) ?? null
  const dateShort = packetDateShort(day.date)
  return {
    iso: day.iso,
    date: day.date,
    campuses: day.campuses,
    stops: packetStops(day.campuses, routeDay),
    routeDay,
    kitchenName,
    dateShort,
    dateCaps: dateShort.toUpperCase(),
    dateLong: packetDateLong(day.date),
    dateBox: packetDateBox(day.date),
  }
}

/**
 * The header's right side on the kitchen's pages (the Oct 21 canvas). The caterer's name prints only on a kitchen stop
 * sheet, the copy that leaves the building; never the date: the date stamp says it.
 */
export const KITCHEN_COPY = 'KITCHEN COPY'
